<script lang="ts" setup>
	import type { DoughnutChartContext } from '@/components/Charts/custom-doughnut.vue'
	import useClaimsApi from '~/services/apis/useClaimsApi'

	const props = defineProps<{
		plan: PlanInformationContext | null
		planAnalysis: ClaimAnalysisResponse | null
	}>()

	const { productCategoryType } = useUtils()
	const { getTravelIncidentTypes } = useClaimsApi()
	const incidentTypes = ref<(string | number)[]>([])

	const categoryType = computed(() =>
		productCategoryType(props.plan?.product?.productCategory?.name || '', props.plan?.product?.code_name)
	)

	const totalClaimssMade = computed(() => props.planAnalysis?.total_claims_made || 0)
	const planAnalySis = computed(() => props.planAnalysis || null)
	const totalPremiumPercent = computed(() =>
		Math.ceil(
			Number(planAnalySis.value?.total_amount_claimed || 0) /
				Number(Math.abs((planAnalySis.value?.plan_premium_paid as number) || 0) * 100)
		)
	)

	const highestValues = (list: Array<number | string>) => {
		const indexedArray = list.map((value, index) => ({ value, index }))

		type IndexedValue = typeof indexedArray
		const max = indexedArray.reduce((maxValues: IndexedValue, curr: IndexedValue[number]) => {
			if (!maxValues.length && !curr.value) {
				return []
			} else if (!maxValues.length || (curr.value && curr.value > maxValues[0].value)) {
				return [curr]
			} else if (curr.value && curr.value === maxValues[0].value) {
				return [...maxValues, curr]
			}
			return maxValues
		}, [])

		return max
	}
	const highestValue = (list: Array<number>) => {
		const indexedArray = list.map((value, index) => ({ value, index }))
		return indexedArray.reduce((max, curr) => {
			return curr.value > max.value ? curr : max
		})
	}

	const plansChart = computed<{
		[id: string]: {
			top: {
				color?: string
				value: (number | string)[]
			}
			charts: DoughnutChartContext
			label?: string
		}
	}>(() => {
		const gadgetCharts = {
			datasets: [
				{
					data: [
						planAnalySis.value?.screen_damage_claimed || 0,
						planAnalySis.value?.liquid_damage_claimed || 0,
						planAnalySis.value?.theft_claimed || 0,
						planAnalySis.value?.burglary_claimed || 0
					],
					backgroundColor: ['#12B76A', '#2E90FA', '#EE46BC', '#7A5AF8'],
					borderRadius: 10
				}
			],
			labels: ['Screen damage', 'Liquid Damage', 'Theft', 'burglary']
		}

		const healthCharts = {
			datasets: [
				{
					data: [5000, 5000],
					backgroundColor: ['#EE46BC', '#7A5AF8'],
					borderRadius: 10
				}
			],
			labels: ['Virtual Consultation', 'Hospital Visit']
		}

		const autoCharts = {
			datasets: [
				{
					data: [
						planAnalySis.value?.stolen_vehicle || 0,
						planAnalySis.value?.accidental_damage || 0,
						planAnalySis.value?.own_damage || 0
					],
					backgroundColor: ['#2E90FA', '#7A5AF8', '#FB6514'],
					borderRadius: 10
				}
			],
			labels: ['Stolen Vehicle', 'Accidental Damage', 'Loss/ Damage of personal effect']
		}
		const travelCharts = {
			datasets: [
				{
					data: [
						planAnalySis.value?.missing_luggage || 0,
						planAnalySis.value?.medical_expenses || 0,
						planAnalySis.value?.delayed_flight || 0
					],
					backgroundColor: ['#2E90FA', '#EE46BC', '#12B76A'],
					borderRadius: 10
				}
			],
			labels: [...incidentTypes.value]
		}

		const getHighestLabels = (arr: (number | string)[], compare: string[]) => {
			return highestValues(arr).map(item => compare[item.index])
		}

		return {
			health: {
				top: {
					value: getHighestLabels(healthCharts.datasets[0].data, healthCharts.labels),
					color:
						healthCharts.datasets[0].backgroundColor[highestValue(healthCharts.datasets[0].data).index] ||
						healthCharts.datasets[0].backgroundColor[0]
				},
				charts: healthCharts,
				label: 'care assessed'
			},
			gadget: {
				top: {
					value: getHighestLabels(gadgetCharts.datasets[0].data, gadgetCharts.labels),
					color:
						gadgetCharts.datasets[0].backgroundColor[highestValue(gadgetCharts.datasets[0].data).index] ||
						gadgetCharts.datasets[0].backgroundColor[0]
				},
				charts: gadgetCharts,
				label: `claim${(totalClaimssMade.value > 1 && 's') || ''} made`
			},
			auto: {
				top: {
					value: getHighestLabels(autoCharts.datasets[0].data, autoCharts.labels),
					color:
						autoCharts.datasets[0].backgroundColor[highestValue(autoCharts.datasets[0].data).index] ||
						autoCharts.datasets[0].backgroundColor[0]
				},
				charts: autoCharts,
				label: `claim${(totalClaimssMade.value > 1 && 's') || ''} made`
			},
			travel: {
				top: {
					value: getHighestLabels(travelCharts.datasets[0].data, travelCharts.labels),
					color:
						travelCharts.datasets[0].backgroundColor[highestValue(travelCharts.datasets[0].data).index] ||
						travelCharts.datasets[0].backgroundColor[0]
				},
				charts: travelCharts,
				label: `claim${(totalClaimssMade.value > 1 && 's') || ''} made`
			}
		}
	})

	const chartData = computed(() => {
		if (categoryType.value?.isGadget) {
			return plansChart.value.gadget
		} else if (categoryType.value.isHealth) {
			return plansChart.value.health
		} else if (categoryType.value?.isAuto) {
			return plansChart.value.auto
		} else if (categoryType.value?.isTravel) {
			return plansChart.value.travel
		}
		return null
	})

	onMounted(async () => {
		if (categoryType.value?.isTravel) {
			const result = await getTravelIncidentTypes()
			incidentTypes.value = result.map(item => item.value)
		}
	})
</script>

<template>
	<div>
		<div v-if="chartData" class="flex flex-wrap items-center gap-x-5 gap-y-9 pt-10">
			<h3 class="text-sm font-medium text-gray-600 sm:hidden"> Total Number of Claims Made </h3>

			<div class="flex max-w-xl flex-wrap-reverse items-center gap-x-10 gap-y-5 sm:flex-wrap sm:border-r sm:pr-5">
				<div class="flex w-full flex-wrap items-center justify-between gap-y-3">
					<h3 class="hidden text-sm font-medium text-gray-600 sm:flex"> Total Number of Claims Made </h3>
					<div
						:class="{ flex: chartData.top.value.length, hidden: !chartData.top.value.length }"
						class="items-start gap-x-3 sm:translate-y-16"
					>
						<div
							class="mt-1 h-[8px] w-[8px] flex-none rounded-full"
							:style="{ backgroundColor: chartData.top.color }"
						/>

						<ul class="relative space-y-0.5">
							<li class="flex items-center">
								<span class="text-[10px] capitalize text-gray-500">
									{{ chartData.label || '' }}
								</span>
							</li>
							<li
								v-for="(item, index) in chartData.top.value"
								:key="index"
								class="py-1 text-sm font-medium text-gray-700"
							>
								{{ item }}
							</li>
						</ul>
					</div>
				</div>
				<charts-custom-doughnut
					:chart-data="chartData.charts"
					use-labels
					class="w-auto"
					:total="totalClaimssMade"
					:description="chartData?.label || ''"
				/>
			</div>

			<my-cover-assessment
				label="Amount claimed against
premium paid"
				:percentage="totalPremiumPercent"
			/>
		</div>
	</div>
</template>
